import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range.js";

import { generateClient } from "aws-amplify/api";
import { retryAfterCognitoLoginMismatch } from "../../utils/amplifyAuthRecovery";
import { safeGraphqlSubscribe } from "./graphqlSubscriptions";
import { EVENTS_PAGE_LIMIT, fetchAllEventPages } from "./eventPagination";
import { scheduleDonationSync } from "./donations";

export const client = generateClient();
const graphql = client.graphql.bind(client);
// additionalHeaders: per-request headers (Teams: x-el-owner for one event's
// account, components/teams/workspace.js ownerHeaders).
client.graphql = (options, additionalHeaders) => {
  const result = graphql(options, additionalHeaders);

  if (!result?.then) {
    return result;
  }

  return result.catch((error) =>
    retryAfterCognitoLoginMismatch(error, () =>
      graphql(options, additionalHeaders)
    )
  );
};

import sentry from "../../sentry";
import analytics from "../../analytics";
import { Platform } from "react-native";
import { t } from "@/localization";
import {
  getWorkspaceOwner,
  ownerHeaders,
  rememberOwnEvent,
} from "../teams/workspace";
import { isReadableMessage } from "../teams/errors";

const LOAD_DATA_ERROR_MESSAGE = t(
  "Could not load data. Please check your internet connection. Ensure the timezone, date and time on your device are correct."
);

export const eventFragment = `
  donation_widget_enabled
  donation_overlay_enabled
  donation_show_in_guestbook
  donation_button_label
  donation_goal
  donation_default_amount
  donation_organizations
  donation_environment
  donation_fundraiser_id
  donation_fundraiser_url
  donation_partner_key
  donation_widget_host
  donation_overlay_url
  donation_text_keyword
  donation_setup_status
  donation_updated_at

  id
  slug
  title
  alias
  event_datetime_utc
  event_window_start_datetime_utc
  event_window_end_datetime_utc
  event_location
  event_timezone
  event_cover_path
  event_status
  archived
  datetime_expiry
  event_privacy
  event_type
  is_published
  stream_key
  is_test
  stream_id
  playlist_url
  broadcaster
  event_privacy
  guestbook_enabled
  prefer_chat
  event_password
  download_enabled
  vod_converted
  video_uploaded
  datetime_uploaded
  datetime_broadcast_started
  datetime_broadcast_stopped
  datetime_broadcast_finished
  email_pref
  vod_filename
  liveViewers
  company_name
  company_url
  logo_path
  description
  custom_date_display
  embedlink
  days
  days_paid
  live_only
  stripe_connected_account_id
  stripe_price_id
  stripe_price_id_test
  hide_viewer_count
  clipFrom
  clipTo
  pricing_mode
  custom_price {
    amount
    currency
  }
  custom_price_per_day {
    amount
    currency
  }
  per_day_tickets_enabled
  sell_download_enabled
  pass_fees_enabled
  download_price {
    amount
    currency
  }
  replay_price {
    amount
    currency
  }
  customer_chooses_price
  customer_chooses_price_default {
    amount
    currency
  }
  links {
    title
    url
    file_upload
  }
  room_of
  room_index
  room_label
  room_noun
  room_slugs
  rooms_ticketing
`;

export const guestsFragment = `
  guests {
    id
    name 
    email
    unsubscribed
    was_live
    was_past
    was_upcoming
    source
  }
`;

export const eventsQuery = `
  query myEvents {
    myEvents {
      ${eventFragment}
    }
  }
`;

export const eventsPageQuery = `
  query myEventsPage($limit: Int, $nextToken: String) {
    myEventsPage(limit: $limit, nextToken: $nextToken) {
      items {
        ${eventFragment}
      }
      nextToken
    }
  }
`;

export const eventsBareQuery = `
  query myEvents {
    myEvents {
      id
      is_test
      is_published
    }
  }
`;

export const profileFragment = `
  username
  credits
  subscription_status
  subscription_access_expiry_datetime
  subscription_plan
  first_name
  last_name
  email
  logo_path
  company_name
  no_branding
  company_url
  account_type
  registered_datetime
  registered_from
  account_type
  custom_domain
  broadcaster_role
  role_other
  onboarding_done
  stripeCustomerId
  prefer_multiple_tests
  legacy_pricing
  ga
  gclid
  fbp
  fbc
  pmf_filled
  events {
    id
    is_test
    is_published
  }
`;

export const myProfileQuery = `
  query {
    broadcasterProfile {
      ${profileFragment}
    }
  }
`;

export const myEventQuery = `
  query MyEvent($slug: String!) {
    
    myEvent(slug: $slug) {
      ${eventFragment}
    }
  }
`;

export const subscriptionEventUpdated = `
  subscription eventUpdated($broadcaster: String! $slug: String!) {
    eventUpdated(broadcaster: $broadcaster, slug: $slug) {
      ${eventFragment}
      ${guestsFragment}
    }
  }
`;

export const subscriptionAnyEventUpdated = `
  subscription eventUpdated($broadcaster: String!) {
    eventUpdated(broadcaster: $broadcaster) {
      ${eventFragment}
    }
  }
`;

export const subscriptionEventUpdatedLiveViewers = `
  subscription eventUpdatedLiveViewers($broadcaster: String!, $slug: String!) {
    eventUpdatedLiveViewers(broadcaster: $broadcaster, slug: $slug) {
      liveViewers
    }
  }
`;

export const createTestEventMutation = `
  mutation CreateTestEvent($broadcaster: String!, $details: TestEventInput!, $force_new_test: Boolean) {
    createTestEvent(
      broadcaster: $broadcaster,
      details: $details,
      force_new_test: $force_new_test
    ) {
       ${eventFragment}
    }
  }
`;

export const createEventMutation = `
  mutation CreateEvent($details: EventInput!) {
    createEvent(
      details: $details
    ) {
      ${eventFragment}
    }
  }
`;

export const deleteAccountMutation = `
  mutation DeleteAccount {
    deleteAccount {
      deleted
    }
  }
`;

export const resetTestMutation = `
  mutation ResetTest($slug: String!) {
    resetEvent(
      slug: $slug
    ) {
      ${eventFragment}
    }
  }
`;

export const deleteEventChunkMutation = `
  mutation DeleteEventChunk($slug: String!, $playlist_index: Int!) {
    deleteEventChunk(slug: $slug, playlist_index: $playlist_index) {
      id
      slug
      broadcaster
      stream_key
      event_status
      datetime_uploaded
      datetime_broadcast_started
      datetime_broadcast_stopped
      datetime_broadcast_finished
      video_uploaded
      vod_converted
      vod_filename
    }
  }
`;

export const updateEventDateTimeMutation = `
  mutation UpdateEventDateTime($slug: String!, $details: EventDateTimeInput!) {
    updateEventDateTime(
      slug: $slug
      details: $details
    ) {
      ${eventFragment}
    }
  }
`;

export const updateEventMetaMutation = `
  mutation UpdateEventMeta($slug: String!, $details: EventMetaInput!) {
    updateEventMeta(
      slug: $slug
      details: $details
    ) {
      ${eventFragment}
    }
  }
`;

export const updateEventStripeOptionsMutation = `
  mutation UpdateEventStripeOptions($slug: String!, $details: EventStripeInput!) {
    updateEventStripeOptions(
      slug: $slug
      details: $details
    ) {
      ${eventFragment}
    }
  }
`;

export const addRoomMutation = `
  mutation AddRoom($slug: String!, $room_noun: String, $room_label: String) {
    addRoom(slug: $slug, room_noun: $room_noun, room_label: $room_label) {
      ${eventFragment}
    }
  }
`;

export const updateRoomMutation = `
  mutation UpdateRoom($slug: String!, $details: RoomInput!) {
    updateRoom(slug: $slug, details: $details) {
      ${eventFragment}
    }
  }
`;

// Email links for guests who watch on the broadcaster's site: pages seen with
// the evt.live/embed.js loader, the ones confirmed, and the old Embed webpage
// URL. Read only through myEvent, the owner's query, and kept out of
// eventFragment so other event queries never depend on these fields.
export const eventEmbedPagesQuery = `
  query event($slug: String!) {
    myEvent(slug: $slug) {
      slug
      embedlink
      embed_pages_seen
      embed_pages_confirmed
    }
  }
`;

export const updateEventEmbedPagesMutation = `
  mutation UpdateEventEmbedOptions($slug: String!, $details: EventEmbedInput!) {
    updateEventEmbedOptions(
      slug: $slug
      details: $details
    ) {
      slug
    }
  }
`;

export const deleteEventMutation = `
  mutation DeleteEvent($slug: String!) {
    deleteEvent(slug: $slug) {
      ${eventFragment}
    }
  }
`;

export const deleteEventGuestMutation = `
  mutation DeleteGuestById($id: String!) {
    deleteGuestById(guest_id: $id) {
      id
    }
  }
`;

export const updateEventAliasMutation = `
  mutation ($slug: String!, $alias: String!) {
    updateEventAlias(
      slug: $slug,
      alias: $alias
    ) {
      ${eventFragment}
    }
  }
`;

export const updateEventClipMutation = `
  mutation ($slug: String!, $clipFrom: Int, $clipTo: Int) {
    updateEventClip(
      slug: $slug,
      clipFrom: $clipFrom,
      clipTo: $clipTo
    ) {
      ${eventFragment}
    }
  }
`;

const updateEventPrivacyMutation = `
  mutation UpdateEventPrivacy($slug: String!, $details: EventPrivacyInput!) {
    updateEventPrivacy(
      slug: $slug,
      details: $details
    ) {
      ${eventFragment}
    }
  }
`;

const updateEventBrandingMutation = `
  mutation UpdateEventBranding($slug: String!, $details: EventBrandingInput!) {
    updateEventBranding(
      slug: $slug,
      details: $details
    ) {
      ${eventFragment}
    }
  }
`;

export const updateEventUploadStatusMutation = `
  mutation updateEventUploadStatus($slug: String!, $video_uploaded: Boolean!, $vod_filename: String, $notify_guests: Boolean, $vod_converted: Boolean, $mode: String, $insert_index: Int) {
    updateEventUploadStatus(
      slug: $slug,
      video_uploaded: $video_uploaded,
      vod_filename: $vod_filename,
      notify_guests: $notify_guests,
      vod_converted: $vod_converted,
      mode: $mode,
      insert_index: $insert_index
    ) {
      ${eventFragment}
    }
  }
`;

export const publishEventMutation = `
  mutation PublishEvent($slug: String!, $purchaseReceipt: String, $quantity: Int, $datetime_expiry: String, $unlock_product_name: String) {
    publishEvent(
      slug: $slug,
      purchaseReceipt: $purchaseReceipt,
      quantity: $quantity,
      datetime_expiry: $datetime_expiry,
      unlock_product_name: $unlock_product_name
    ) {
      ${eventFragment}
    }
  }
`;

export const inviteGuestsMutation = `
  mutation registerGuests($event_id: String!, $guests: [GuestInput], $sendInvite: Boolean) {
    registerGuests(
      event_id: $event_id,
      guests: $guests,
      sendInvite: $sendInvite
    ) {
      ${guestsFragment}
      ${eventFragment}
    }
  }
`;

export const guestsQuery = `
  query event($slug: String!) {
    myEvent(slug: $slug) {        
      ${guestsFragment}
      room_of
      event_status
      event_privacy
      stream_key
      title
      guestbook_enabled
      is_published
      id
      slug
      alias
      broadcaster
    }
  }
`;

export const commentsQuery = `
  query event($slug: String!) {
    myEvent(slug: $slug) {  
      guests {
        id
        name 
        email
      }
      id
      broadcaster
      slug
      guestbook_enabled
      prefer_chat
      guestbook_messages {
        id
        message_datetime_utc
        message
        image_filename
        pinned
        donor_name
        donation_amount
        donation_currency
        guest {
          id
          name
          email
        }
      }
    }
  }
`;

export const deleteCommentMutation = `
  mutation DeleteComment($event_id: String!, $id: String!) {
    deleteGuestbookComment(
      event_id: $event_id,
      id: $id
    )
  }
`;

export const sendPinnedMessageMutation = `
  mutation sendPinnedMessage($event_id: String!, $message: String!) {
    sendPinnedMessage(
      event_id: $event_id,
      message: $message
    ) {
      id
      event_id
      message
      message_datetime_utc
      pinned
      guest {
        id
        name
      }
    }
  }
`;

export const guestbookSubscription = `
  subscription getmessages($event_id: String!) {
    messageSent(event_id: $event_id) {
      id
      event_id
      message
      message_datetime_utc
      image_filename
      pinned
      guest {
        id
        name
      }
    }
  }
`;

const updateEmailMutation = `
  mutation UpdateEmail($email: String!) {
    updateEmail(
      email: $email
    ) {
      email
    }
  }
`;

const updateBroadcasterProfileMutation = `
  mutation UpdateBroadcasterProfile($details: BroadcasterProfileInput!) {
    updateBroadcasterProfile(
      details: $details
    ) {
      username
    }
  }
`;

const resetLogoMutation = `mutation UpdateBroadcasterProfile($broadcaster: String!) {
  updateBroadcasterLogo(broadcaster: $broadcaster, logo_path: "") {
    username
    logo_path
  }
}`;

const updateEventCoverMutation = `
  mutation UpdateEventCoverPath($broadcaster: String!, $slug: String!, $event_cover_path: String!) {
    updateEventCoverPath(broadcaster: $broadcaster, slug: $slug, event_cover_path: $event_cover_path) {
      ${eventFragment}
    }
  }
`;

const creditBroadcasterMutation = `
  mutation creditBroadcaster(
    $broadcaster: String!
    $credits: Int!
    $purchaseReceipt: String
    $productId: String
    $purchaseToken: String
    $transactionId: String
  ) {
    creditBroadcaster(
      broadcaster: $broadcaster
      credits: $credits
      purchaseReceipt: $purchaseReceipt
      productId: $productId
      purchaseToken: $purchaseToken
      transactionId: $transactionId
    ) {
      username
      email
    }
  }`;

const videoStatsQuery = `
  query event($broadcaster: String!, $slug: String!) {
    event(broadcaster: $broadcaster, slug: $slug) {
      id 
      unique_viewers
      live_viewers  
    }
  }
`;

export const videoStatsSimple = async (broadcaster, slug) => {
  try {
    const {
      data: { event },
    } = await client.graphql({
      query: videoStatsQuery,
      variables: { broadcaster, slug },
    });
    return event;
  } catch (err) {
    console.error(err);
  }
};

// AppSync's publishEvent function drops the Lambda's error: a failed unlock
// comes back as data.publishEvent === null, not as a GraphQL error.
const requireUnlockedEvent = (event) => {
  if (!event) {
    throw new Error("The server did not confirm the event unlock");
  }
  return event;
};

export const unlockWithCredit = async (slug, quantity, options = {}) => {
  try {
    const result = await client.graphql({
      query: publishEventMutation,
      variables: {
        slug,
        quantity,
      },
    });
    requireUnlockedEvent(result?.data?.publishEvent);
    return result;
  } catch (err) {
    console.error(err);
    if (options.throwOnError) {
      throw err;
    }
  }
};

const getCreditProductName = (product, productId) => {
  if (product && typeof product === "object") {
    for (const field of ["title", "name", "displayName"]) {
      const value = product[field];
      if (typeof value === "string" && value.trim()) {
        return value.trim();
      }
    }
  }
  return productId || null;
};

const getPurchaseReceiptForVerification = (purchase) => {
  if (!purchase || typeof purchase !== "object") {
    return null;
  }

  if (purchase.transactionReceipt || purchase.dataAndroid) {
    return purchase.transactionReceipt || purchase.dataAndroid;
  }

  if (purchase.purchaseToken && purchase.productId) {
    // On iOS purchaseToken is the StoreKit 2 signed transaction (JWS); the
    // platform keeps the server from ever treating it as a Google token.
    return JSON.stringify({
      productId: purchase.productId,
      purchaseToken: purchase.purchaseToken,
      packageName: purchase.packageName,
      platform: Platform.OS,
    });
  }

  return null;
};

export const creditBroadcaster = async (
  broadcaster,
  purchase,
  product,
  options = {}
) => {
  let quantity = 1;
  let revenue = 50;
  switch (purchase.productId) {
    case "package_5":
    case "package_5_new":
      quantity = 5;
      revenue = 200;
      break;
    case "package_10":
    case "package_10_new":
      quantity = 10;
      revenue = 350;
      break;
    case "package_25":
    case "package_25_new":
      quantity = 25;
      revenue = 750;
      break;
    case "package_50":
    case "package_50_new":
      quantity = 50;
      revenue = 1250;
      break;
  }

  try {
    const result = await client.graphql({
      query: creditBroadcasterMutation,
      variables: {
        broadcaster,
        credits: quantity,
        purchaseReceipt: getPurchaseReceiptForVerification(purchase),
        productId: purchase?.productId,
        purchaseToken: purchase?.purchaseToken,
        transactionId: purchase?.transactionId,
      },
    });
    if (!result?.data?.creditBroadcaster) {
      throw new Error("The server did not confirm the credits");
    }
    // TODO: move to serverside or RevenueCat
    await analytics.track("Purchase", {
      plarform: Platform.OS,
      platform: Platform.OS,
      product: purchase?.productId,
      product_name: getCreditProductName(product, purchase?.productId),
      currency: product?.currency || "USD",
      revenue,
      product_price_id: purchase.productId,
      source: "appstore",
      numEvents: quantity,
      transaction_id: purchase.transactionId,
    });
  } catch (err) {
    console.error(err);
    if (options.throwOnError) {
      throw err;
    }
  }
};

const cleanEmpty = (obj) => {
  const clone = { ...obj };
  Object.keys(clone).forEach((key) => (clone[key] = obj[key] || null));
  return clone;
};

// Throws when the save fails, so the profile's auto-save can say so.
export const updateProfile = async (details) => {
  try {
    await client.graphql({
      query: updateBroadcasterProfileMutation,
      variables: {
        details: cleanEmpty({ broadcaster_role: null, ...details }),
      },
    });
  } catch (err) {
    console.error(err);
    throw new Error(t("Couldn't save your profile"));
  }
};

// TODO: Security risk, make a new mutation
export const resetLogo = async (broadcaster) => {
  try {
    await client.graphql({
      query: resetLogoMutation,
      variables: { broadcaster },
    });
  } catch (err) {
    console.error(err);
  }
};

export const publishEvent = async (
  slug,
  purchase,
  currency,
  price,
  coupon,
  datetime_expiry,
  unlock_product_name,
  options = {}
) => {
  const purchaseReceipt = getPurchaseReceiptForVerification(purchase);
  const variables = {
    slug,
    purchaseReceipt,
    datetime_expiry,
    ...(datetime_expiry && unlock_product_name ? { unlock_product_name } : {}),
  };
  const payloadWithoutReceipt = {
    ...variables,
    purchaseReceipt: purchaseReceipt ? "<present>" : "<missing>",
    includes_unlock_product_name: !!(datetime_expiry && unlock_product_name),
  };

  try {
    console.log("publishEvent payload", payloadWithoutReceipt);

    const {
      data: { publishEvent },
    } = await client.graphql({
      query: publishEventMutation,
      variables,
    });

    console.log(publishEvent);
    return requireUnlockedEvent(publishEvent);
  } catch (err) {
    console.error(err);
    if (options.throwOnError) {
      throw err;
    }
  }
};

const executeGraphQL = async (query, variables) => {
  try {
    const { data } = await client.graphql({
      query,
      variables,
    });
    return data;
  } catch (res) {
    const e = res.errors ? res.errors : null;
    console.log(e);
    console.log("in query", query);
    console.log("variables", variables);

    sentry.withScope((scope) => {
      scope.setExtra("response", res);
      scope.setExtra("variables", variables);
      scope.setExtra("query", query);
      // Capture a real Error with a meaningful, groupable message. Passing the raw
      // errors array produced "Object captured as exception" (EVENTLIVE-IL-2D5) and
      // passing null on transport failures produced "Error: null" (EVENTLIVE-IL-2W4),
      // neither of which is actionable in Sentry.
      let captured;
      if (res instanceof Error) {
        captured = res;
      } else if (e && e.length) {
        captured = new Error(
          `GraphQL: ${e
            .map((g) => g.message || g.errorType || "unknown")
            .join("; ")
            .slice(0, 300)}`
        );
      } else {
        captured = new Error("GraphQL request failed with no error payload");
      }
      sentry.captureException(captured);
    });

    // Teams: the server's refusals in someone else's account are sentences
    // meant for people ("You don't have access to that in this account.").
    const teamsMessage =
      getWorkspaceOwner() && e?.[0]?.errorType ? e[0].message : null;
    if (teamsMessage && isReadableMessage(teamsMessage)) {
      const err = new Error(teamsMessage);
      err.isTeamsError = true;
      err.errors = e;
      throw err;
    }

    const err = new Error(LOAD_DATA_ERROR_MESSAGE);
    err.isUserRecoverable = true;
    throw err;
  }
};

export const createTest = async (broadcaster, force_new_test = false) => {
  const { createTestEvent } = await executeGraphQL(createTestEventMutation, {
    broadcaster,
    details: {
      title: "Test Event",
      event_timezone: moment.tz.guess(),
    },
    force_new_test,
  });

  // Tests stay in your own account, also while working in another one.
  rememberOwnEvent(createTestEvent);
  return createTestEvent;
};

export const fetchEventsPage = async ({
  limit = EVENTS_PAGE_LIMIT,
  nextToken = null,
} = {}) => {
  const { myEventsPage } = await executeGraphQL(eventsPageQuery, {
    limit,
    nextToken,
  });
  return {
    items: myEventsPage?.items ?? [],
    nextToken: myEventsPage?.nextToken ?? null,
  };
};

export const fetchEvents = async ({ limit = EVENTS_PAGE_LIMIT, onPage } = {}) =>
  fetchAllEventPages({
    fetchPage: (nextToken) => fetchEventsPage({ limit, nextToken }),
    onPage,
  });

export const fetchNumberOfEvents = async () => {
  const {
    data: { myEvents },
  } = await client.graphql({
    query: eventsBareQuery,
  });

  return {
    publishedEvents: myEvents.filter((e) => !e.is_test && e.is_published)
      .length,
    unpublishedEvents: myEvents.filter((e) => !e.is_test && !e.is_published)
      .length,
    testEvents: myEvents.filter((e) => e.is_test).length,
  };
};

// owner: the event's broadcaster when known. While working in someone else's
// account it names the account the event is in (your own test events stay
// yours); in your own account it changes nothing.
export const fetchEvent = async (slug, { owner } = {}) => {
  const { data } = await client.graphql(
    {
      query: myEventQuery,
      variables: { slug },
    },
    ownerHeaders(owner)
  );
  rememberOwnEvent(data?.myEvent);
  return data;
};

// Rooms (components/rooms/rooms.js): adds a room to the event `slug` belongs
// to (the primary or one of its rooms). Unlocked like any event; returns the
// new room, unpublished when there weren't enough credits.
export const addRoom = async ({ slug, room_noun, room_label }) => {
  const {
    data: { addRoom: room },
  } = await client.graphql({
    query: addRoomMutation,
    variables: { slug, room_noun, room_label },
  });
  return room;
};

// details: { room_label } on any room, { rooms_ticketing } on the primary.
export const updateRoom = async (slug, details) => {
  const {
    data: { updateRoom: event },
  } = await client.graphql({
    query: updateRoomMutation,
    variables: { slug, details },
  });
  return event;
};

const byRoomIndex = (a, b) => (a?.room_index || 0) - (b?.room_index || 0);

// The primary and its rooms (deleted ones left out), for room switchers.
export const fetchRoomGroup = async (event) => {
  if (!event?.slug) return { primary: null, rooms: [] };
  const load = async (slug) => {
    try {
      const data = await fetchEvent(slug, { owner: event.broadcaster });
      return data?.myEvent || null;
    } catch (err) {
      console.error(err);
      return null;
    }
  };
  const primary = event.room_of
    ? await load(event.room_of)
    : Array.isArray(event.room_slugs)
    ? event
    : await load(event.slug);
  if (!primary) return { primary: null, rooms: [] };
  const slugs = Array.isArray(primary.room_slugs) ? primary.room_slugs : [];
  const rooms = (await Promise.all(slugs.map(load)))
    .filter((room) => room && room.room_of === primary.slug)
    .sort(byRoomIndex);
  return { primary, rooms };
};

export const fetchEventByStreamKey = async (stream_key) => {
  const {
    data: { event },
  } = await client.graphql({
    query: `
      query($stream_key: String!) {
        event: event_by_stream_key(stream_key: $stream_key) {
          ${eventFragment}
        }
      }
    `,
    variables: { stream_key },
  });

  return event;
};

export const deleteEvent = async (slug) => {
  await client.graphql({
    query: deleteEventMutation,
    variables: { slug },
  });
  return true;
};

export const deleteGuestById = async (id) => {
  await client.graphql({
    query: deleteEventGuestMutation,
    variables: { id },
  });
  return true;
};

export const fetchProfile = async () => {
  const {
    data: { broadcasterProfile },
  } = await client.graphql({
    query: myProfileQuery,
  });
  return broadcasterProfile;
};

export const subscribeToEventUpdates = (broadcaster, slug, onUpdate) => {
  const subscription = safeGraphqlSubscribe({
    client,
    request: {
      query: subscriptionEventUpdated,
      variables: { broadcaster, slug },
    },
    observer: {
      next: (payload) => {
        // Amplify client payload shape can differ by runtime/version.
        const updatedEvent =
          payload?.data?.eventUpdated ?? payload?.value?.data?.eventUpdated;
        if (updatedEvent) {
          onUpdate(updatedEvent);
        }
      },
      error: (err) =>
        console.log("[LiveViewersAndroid] subscribeToEventUpdates:error", err),
    },
  });

  return () => {
    subscription.unsubscribe();
  };
};

export const subscribeToEventLiveViewerUpdates = (
  broadcaster,
  slug,
  onUpdate
) => {
  const subscription = safeGraphqlSubscribe({
    client,
    request: {
      query: subscriptionEventUpdatedLiveViewers,
      variables: { broadcaster, slug },
    },
    observer: {
      next: (payload) => {
        const liveViewers =
          payload?.data?.eventUpdatedLiveViewers?.liveViewers ??
          payload?.value?.data?.eventUpdatedLiveViewers?.liveViewers ??
          payload?.data?.eventUpdatedLiveViewers ??
          payload?.value?.data?.eventUpdatedLiveViewers;
        onUpdate({ liveViewers });
      },
      error: (err) =>
        console.log(
          "[LiveViewersAndroid] subscribeToEventLiveViewerUpdates:error",
          err
        ),
    },
  });

  return () => {
    subscription.unsubscribe();
  };
};

//TODO: fix, this crashes on android in airplane mode
export const subscribeToEventsUpdates = (broadcaster, onUpdate) => {
  const subscription = safeGraphqlSubscribe({
    client,
    request: {
      query: subscriptionAnyEventUpdated,
      variables: { broadcaster },
    },
    observer: {
      next: ({ data }) => {
        onUpdate(data.eventUpdated);
      },
      error: (err) => console.log("error caught", err),
    },
  });

  return () => {
    subscription.unsubscribe();
  };
};

export const createEvent = async ({
  title,
  event_datetime,
  event_timezone,
  event_type,
  // event_privacy,
  event_location,
  alias,
  days,
  // guestbook_enabled,
  // event_password,
  // download_enabled,
}) => {
  const event_date = moment(event_datetime).format("YYYY-MM-DD");
  const event_time = moment(event_datetime).format("HH:mm");

  const date = moment.tz(`${event_date} ${event_time}`, event_timezone);

  const event_datetime_utc = date.utc().format();

  const {
    errors,
    data: { createEvent: result },
  } = await client.graphql({
    query: createEventMutation,
    variables: {
      details: {
        title,
        event_date,
        event_time,
        event_datetime_utc,
        event_timezone,
        event_location,
        guestbook_enabled: true,
        event_type,
        alias: alias.toLowerCase(),
        days,
      },
    },
  });

  return result;
};

export const deleteAccount = async () => {
  const res = await client.graphql({
    query: deleteAccountMutation,
    variables: {},
  });
  return res;
};

export const resetEvent = async (slug) => {
  const {
    errors,
    data: { resetEvent: result },
  } = await client.graphql({
    query: resetTestMutation,
    variables: { slug },
  });

  return result;
};

export const deleteEventChunk = async (slug, playlist_index) => {
  const {
    errors,
    data: { deleteEventChunk: result },
  } = await client.graphql({
    query: deleteEventChunkMutation,
    variables: { slug, playlist_index },
  });

  if (errors) {
    throw new Error("Could not delete event chunk");
  }

  return result;
};

// `previousTitle` is the title before this save; when it changes on an event
// with a fundraiser, the Pledge fundraiser name is synced in the background.
export const updateEventDetails = async (event, { previousTitle } = {}) => {
  const { slug, title, event_location, event_type, links } = event;

  const {
    data: { updateEventMeta },
  } = await client.graphql({
    query: updateEventMetaMutation,
    variables: {
      slug,
      details: {
        title,
        event_location,
        links,
        event_type,
      },
    },
  });

  if (
    typeof previousTitle === "string" &&
    updateEventMeta?.title &&
    updateEventMeta.title !== previousTitle
  ) {
    scheduleDonationSync(updateEventMeta);
  }
  return {
    ...event,
    ...updateEventMeta,
  };
};

export const updateEventStripeDetails = async (slug, details) => {
  const {
    data: { updateEventStripeOptions },
  } = await client.graphql({
    query: updateEventStripeOptionsMutation,
    variables: {
      slug,
      details,
    },
  });

  return {
    ...updateEventStripeOptions,
  };
};

export const updateEventDateTimeDetails = async (event) => {
  const { slug, event_timezone, event_datetime, days = 1 } = event;

  const event_date = moment(event_datetime).format("YYYY-MM-DD");
  const event_time = moment(event_datetime).format("HH:mm");

  const {
    data: { updateEventDateTime },
  } = await client.graphql({
    query: updateEventDateTimeMutation,
    variables: {
      slug,
      details: {
        event_timezone,
        event_date,
        event_time,
        days,
      },
    },
  });

  return {
    ...event,
    ...updateEventDateTime,
  };
};

export const getEventEmbedPages = async (slug) => {
  const {
    data: { myEvent },
  } = await client.graphql({
    query: eventEmbedPagesQuery,
    variables: { slug },
  });
  return myEvent;
};

// details: { embed_page, embed_page_action: "confirm" | "ignore" | "forget" }
// or { embedlink: null } to remove the old Embed webpage URL. Returns the
// pages as myEvent reads them afterwards.
export const updateEventEmbedPages = async ({ details, slug }) => {
  await client.graphql({
    query: updateEventEmbedPagesMutation,
    variables: { slug, details },
  });
  return getEventEmbedPages(slug);
};

export const updateEventAlias = async ({ alias, slug }) => {
  if (!alias) throw new Error(t("Link required"));

  const {
    errors,
    data: { updateEventAlias: result },
  } = await client.graphql({
    query: updateEventAliasMutation,
    variables: {
      slug,
      alias: alias.toLowerCase(),
    },
  });

  return result;
};

export const updateEventClip = async ({ slug, clipFrom, clipTo }) => {
  const {
    errors,
    data: { updateEventClip: result },
  } = await client.graphql({
    query: updateEventClipMutation,
    variables: {
      slug,
      clipFrom,
      clipTo,
    },
  });

  return result;
};

export const updateEventPrivacy = async ({
  event_privacy,
  guestbook_enabled,
  event_password,
  download_enabled,
  prefer_chat,
  hide_viewer_count,
  slug,
  email_pref,
  live_only,
}) => {
  const details = Object.fromEntries(
    Object.entries({
      event_privacy,
      guestbook_enabled,
      event_password,
      download_enabled,
      prefer_chat,
      hide_viewer_count,
      email_pref,
      live_only,
    }).filter(([, value]) => value !== undefined)
  );

  const {
    errors,
    data: { updateEventPrivacy: result },
  } = await client.graphql({
    query: updateEventPrivacyMutation,
    variables: {
      slug,
      details,
    },
  });

  if (errors) {
    throw new Error("Could not update privacy settings");
  }

  return result;
};

export const updateEventBranding = async ({
  slug,
  description,
  logo_path,
  company_name,
  company_url,
  custom_date_display,
}) => {
  const details = {
    description,
    logo_path,
    company_name,
    company_url,
    custom_date_display,
  };

  const {
    errors,
    data: { updateEventBranding: result },
  } = await client.graphql({
    query: updateEventBrandingMutation,
    variables: {
      slug,
      details,
    },
  });

  if (errors) {
    throw new Error("Could not update branding settings");
  }

  return result;
};

export const fetchGuests = async (slug) => {
  const {
    data: { myEvent },
  } = await client.graphql({
    query: guestsQuery,
    variables: { slug },
  });
  return myEvent;
};

// How many people are on the guest list (ticket buyers and invited guests),
// without loading the list. null when the answer has no number.
export const fetchGuestsCount = async (slug) => {
  const {
    data: { myEvent },
  } = await client.graphql({
    query: `
      query GuestsCount($slug: String!) {
        myEvent(slug: $slug) {
          id
          guestsCount
        }
      }
    `,
    variables: { slug },
  });
  return Number.isInteger(myEvent?.guestsCount) ? myEvent.guestsCount : null;
};

export const checkAliasAvailable = async (alias) => {
  const {
    data: { event },
  } = await client.graphql({
    query: `
      query($alias: String!) {
        event: event_by_alias(alias: $alias) {
          id
        }
      }
    `,
    variables: { alias },
    // authMode: "",
  });

  return !event;
};

export const getEventByAlias = async (alias) => {
  const {
    data: { event },
  } = await client.graphql({
    query: `
      query($alias: String!) {
        event: event_by_alias(alias: $alias) {
          ${eventFragment}
        }
      }
    `,
    variables: { alias },
  });

  return event;
};

export const inviteGuests = async (event, guests) => {
  const {
    data: { registerGuests },
    errors,
  } = await client.graphql({
    query: inviteGuestsMutation,
    variables: {
      event_id: event.id,
      guests,
      sendInvite: true,
    },
  });

  return registerGuests;
};

export const fetchGuestbook = async (slug) => {
  const {
    data: { myEvent },
  } = await client.graphql({
    query: commentsQuery,
    variables: { slug },
  });

  return myEvent;
};

export const deleteComment = async (event, id) => {
  const { errors, data: result } = await client.graphql({
    query: deleteCommentMutation,
    variables: { event_id: event.id, id },
  });

  return result;
};

export const sendPinnedMessage = async (event, message) => {
  await client.graphql({
    query: sendPinnedMessageMutation,
    variables: { event_id: event.id, message },
  });
};

export const resetThumbnail = async (event) => {
  const { broadcaster, slug } = event;
  await client.graphql({
    query: updateEventCoverMutation,
    variables: {
      broadcaster,
      slug,
      event_cover_path:
        "https://eventliveprod-posters.s3.amazonaws.com/event-live-default.jpg",
    },
  });
};

export const resetVideo = async (slug) => {
  return await client.graphql({
    query: updateEventUploadStatusMutation,
    variables: {
      slug,
      vod_filename: null,
      video_uploaded: false,
      notify_guests: false,
      vod_converted: false,
    },
  });
};

export const convertVideo = async (
  slug,
  vod_filename,
  notify_guests = false,
  mode = null,
  insert_index = null
) => {
  return await client.graphql({
    query: updateEventUploadStatusMutation,
    variables: {
      slug,
      vod_filename,
      video_uploaded: true,
      notify_guests,
      vod_converted: false,
      mode,
      insert_index,
    },
  });
};

export const updateEmail = async ({ email }) => {
  const {
    errors,
    data: { updateEmail },
  } = await client.graphql({
    query: updateEmailMutation,
    variables: { email: email.toLowerCase() },
  });

  if (errors) {
    throw new Error("Could not change email");
  }
  return updateEmail;
};

export const subToGuestbook = (eventId, onUpdate) => {
  return safeGraphqlSubscribe({
    client,
    request: {
      query: guestbookSubscription,
      variables: { event_id: eventId },
    },
    observer: {
      next: ({ data }) => {
        onUpdate(data.messageSent);
      },
      error: (err) => console.log("error caught", err),
    },
  });
};
